<template>
  <div class="detail-page" v-loading="loading">
    <div class="page-header">
      <el-button
        class="back-btn"
        type="primary"
        round
        size="small"
        @click="$emit('close')"
      >返回</el-button>
      <h2 class="page-title">设备详情</h2>
    </div>

    <!-- 基础信息 -->
    <section class="section">
      <div class="section-title">基础信息</div>
      <el-descriptions :column="3" border>
        <el-descriptions-item label="设备名称">
          <el-input :value="detail.name" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="规格型号">
          <el-input :value="detail.model" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="出厂编号">
          <el-input :value="detail.factory_no" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="科室">
          <el-input :value="detail.department" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="生产厂家">
          <el-input :value="detail.manufacturer" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="生产日期">
          <el-input :value="detail.production_date" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="安装日期">
          <el-input :value="detail.install_date" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="到期日期">
          <el-input :value="detail.warranty_expiry_date" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="使用期限">
          <el-input :value="detail.service_life ? detail.service_life + ' 年' : ''" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="设备价格">
          <el-input :value="detail.price" readonly size="small">
            <template slot="prepend">¥</template>
          </el-input>
        </el-descriptions-item>
        <el-descriptions-item label="使用状态">
          <el-input :value="detail.status_display" readonly size="small" />
        </el-descriptions-item>
        <el-descriptions-item label="固定资产">
          <el-tag :type="detail.is_fixed_asset ? 'success' : 'info'" size="small" effect="plain">
            {{ detail.is_fixed_asset ? '是' : '否' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="备注" :span="3">
          <el-input
            :value="detail.remark"
            type="textarea"
            readonly
            size="small"
            :autosize="{ minRows: 2, maxRows: 4 }"
          />
        </el-descriptions-item>
      </el-descriptions>
    </section>

    <!-- 资质信息 -->
    <section class="section">
      <div class="section-title">资质信息</div>
      <el-descriptions :column="1" border>
        <el-descriptions-item label="合格证">
          <el-tag :type="detail.has_certificate ? 'success' : 'info'" size="small" effect="plain">
            {{ detail.has_certificate ? '有' : '无' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="注册证">
          <el-tag :type="detail.has_registration ? 'success' : 'info'" size="small" effect="plain">
            {{ detail.has_registration ? '有' : '无' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="生产许可证">
          <el-tag :type="detail.has_production_license ? 'success' : 'info'" size="small" effect="plain">
            {{ detail.has_production_license ? '有' : '无' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="营业执照">
          <el-tag :type="detail.has_business_license ? 'success' : 'info'" size="small" effect="plain">
            {{ detail.has_business_license ? '有' : '无' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="资质有效期">
          <el-input :value="detail.expire_date" readonly size="small" />
        </el-descriptions-item>
      </el-descriptions>
    </section>

    <!-- 图片与二维码 -->
    <section class="section">
      <div class="section-title">图片与二维码</div>
      <el-row :gutter="10">
        <el-col :span="8">
          <div class="img-title">设备照片</div>
          <div class="img-box">
            <el-image
              v-if="detail.photo_url"
              :src="detail.photo_url"
              fit="contain"
              :preview-src-list="previewList"
              @error="onImageError('photo')"
            >
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
                <div>照片加载失败</div>
                <div class="img-tip">{{ detail.photo_url }}</div>
              </div>
            </el-image>
            <span v-else class="no-data">暂无</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="img-title">设备铭牌</div>
          <div class="img-box">
            <el-image
              v-if="detail.nameplate_url"
              :src="detail.nameplate_url"
              fit="contain"
              :preview-src-list="previewList"
              @error="onImageError('nameplate')"
            >
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
                <div>铭牌加载失败</div>
                <div class="img-tip">{{ detail.nameplate_url }}</div>
              </div>
            </el-image>
            <span v-else class="no-data">暂无</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="img-title">
            二维码
            <el-tag size="mini" :type="detail.code_type === 'miniprogram' ? 'success' : 'info'" effect="plain" style="margin-left: 6px">
              {{ detail.code_type === 'miniprogram' ? '小程序码' : (detail.code_type === 'qrcode' ? '普通二维码' : '未生成') }}
            </el-tag>
          </div>
          <div class="img-box">
            <img
              v-if="detail.qrcode_url"
              :src="detail.qrcode_url"
              alt="二维码"
              class="qrcode-img"
              @error="onImageError('qrcode')"
            />
            <span v-else class="no-data">暂无</span>
            <div v-if="qrcodeError" class="img-tip" style="color: #f56c6c">{{ qrcodeError }}</div>
          </div>
        </el-col>
      </el-row>
    </section>

    <!-- 资质 PDF -->
    <section class="section">
      <div class="section-title">资质文件</div>
      <div v-if="detail.qualification_url" class="pdf-bar">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-view"
          @click="showPdf = !showPdf"
        >
          {{ showPdf ? '收起资质预览' : '查看资质 PDF' }}
        </el-button>
        <el-button
          type="success"
          size="small"
          icon="el-icon-download"
          @click="downloadPdf"
        >
          下载资质 PDF
        </el-button>
        <span v-if="pdfError" class="pdf-error">{{ pdfError }}</span>
      </div>
      <span v-else class="no-data">暂无资质文件</span>
      <iframe
        v-if="showPdf"
        :src="pdfPreviewUrl"
        class="pdf-frame"
        frameborder="0"
      ></iframe>
    </section>
  </div>
</template>

<script>
import equipmentApi from '../api/equipment'

export default {
  name: 'EquipmentDetail',
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      detail: {},
      loading: false,
      qrcodeError: '',
      pdfError: '',
      showPdf: false,
    }
  },
  computed: {
    previewList() {
      const list = []
      if (this.detail.photo_url) list.push(this.detail.photo_url)
      if (this.detail.nameplate_url) list.push(this.detail.nameplate_url)
      if (this.detail.qrcode_url) list.push(this.detail.qrcode_url)
      return list
    },
    pdfPreviewUrl() {
      if (!this.detail.qualification_url) return ''
      try {
        const url = new URL(this.detail.qualification_url)
        return url.pathname
      } catch (e) {
        return this.detail.qualification_url
      }
    },
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    async fetchDetail() {
      this.loading = true
      this.qrcodeError = ''
      this.pdfError = ''
      try {
        const res = await equipmentApi.detail(this.id)
        this.detail = res.data
      } catch (e) {
        this.$message.error('加载详情失败')
      } finally {
        this.loading = false
      }
    },
    onImageError(type) {
      const msg = {
        photo: '设备照片',
        nameplate: '设备铭牌',
        qrcode: '二维码',
      }[type]
      if (type === 'qrcode') {
        this.qrcodeError = '二维码加载失败，可检查后端服务或文件是否存在'
      }
      console.error(`${msg} 加载失败:`, this.detail[`${type}_url`])
    },
    downloadPdf() {
      if (!this.detail.qualification_url) {
        this.$message.warning('暂无资质文件')
        return
      }
      const link = document.createElement('a')
      link.href = this.detail.qualification_url
      link.download = `${this.detail.name || '设备'}_资质.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.detail-page {
  padding: 0;
  background: transparent;
  font-size: 14px;
  color: #303133;
}

/* 页面头部：返回按钮靠左，标题居中放大 */
.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  min-height: 36px;
}
.back-btn {
  position: absolute;
  left: 0;
}
.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
  text-align: center;
}

.section {
  margin-bottom: 14px;
}
.section-title {
  position: relative;
  margin: 0 0 12px 0;
  padding-left: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}
.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 16px;
  background: #409eff;
  border-radius: 2px;
}

/* 字段标签：统一大小、加粗；内容区使用 el-input 作为只读文本框，字号保持默认 */
.section /deep/ .el-descriptions__header {
  margin-bottom: 0;
}
.section /deep/ .el-descriptions__body .el-descriptions-item__cell {
  padding: 6px 10px !important;
  line-height: 1.4 !important;
}
.section /deep/ .el-descriptions-item__label.is-bordered-label {
  width: 110px;
  color: #303133;
  font-size: 15px;
  font-weight: 700;
  background: #f5f7fa;
}
.section /deep/ .el-descriptions-item__content {
  color: #606266;
}
.section /deep/ .el-input {
  width: 100%;
}
.section /deep/ .el-input__inner {
  height: 30px;
  line-height: 30px;
  color: #606266;
  background: #fff;
}
.section /deep/ .el-textarea__inner {
  color: #606266;
  background: #fff;
}
.section /deep/ .el-input-group__prepend,
.section /deep/ .el-input-group__append {
  padding: 0 8px;
  color: #909399;
}

/* 图片区标题居中、间距紧凑 */
.img-title {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  text-align: center;
}
.img-box {
  width: 100%;
  max-width: 320px;
  height: 180px;
  margin: 0 auto;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: #fafafa;
}
.img-box /deep/ .el-image {
  width: 100%;
  height: 100%;
}
.qrcode-img {
  width: 140px;
  height: 140px;
}
.image-slot {
  text-align: center;
  color: #909399;
  padding: 10px;
}
.image-slot i {
  font-size: 24px;
  margin-bottom: 6px;
}
.img-tip {
  font-size: 11px;
  color: #909399;
  margin-top: 6px;
  word-break: break-all;
  line-height: 1.3;
}
.no-data {
  color: #c0c4cc;
  font-size: 12px;
}

.pdf-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.pdf-error {
  color: #f56c6c;
  font-size: 12px;
}
.pdf-frame {
  width: 100%;
  height: 460px;
  margin-top: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #fafafa;
}
</style>
